'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import {
  isArchivedEntity,
  takeMutableArchiveSelection,
} from '@terra/shared-dto-types';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import {
  getSignalTestingScopeEligibility,
  useApplicationTestingScopeSource,
} from '../../../hooks/useSignalTestingScopeEligibility';
import { useSignalAttackRun } from '../../../hooks/useSignalAttackRun';
import {
  SEND_TO_COPILOT_SIGNAL_LIMIT,
  SEND_TO_NEO_SIGNAL_LIMIT,
} from './signals-bulk-actions.model';
import { useSignalCopilotAttack } from './useSignalCopilotAttack';
import type { SignalsSelection } from './useSignalsSelection';

function useOutOfTestingScopeSelectionCount({
  selection,
  isNetworkTarget,
  isOutOfTestingScope,
}: {
  selection: SignalsSelection;
  isNetworkTarget: boolean;
  isOutOfTestingScope: (signal: GroupedSignal) => boolean;
}) {
  const [count, setCount] = useState(0);
  const {
    selectedCount,
    isAllFilteredSelected,
    selectedSignalIds,
    signalById,
    getSelectedSignals,
  } = selection;

  useEffect(() => {
    if (selectedCount === 0 || isNetworkTarget) {
      setCount(0);

      return;
    }

    if (!isAllFilteredSelected) {
      setCount(
        Array.from(selectedSignalIds)
          .map((signalId) => signalById.get(signalId))
          .filter((signal): signal is GroupedSignal => Boolean(signal))
          .filter(isOutOfTestingScope).length,
      );

      return;
    }

    let isCancelled = false;

    void getSelectedSignals().then((selectedSignals) => {
      if (!isCancelled) {
        setCount(selectedSignals.filter(isOutOfTestingScope).length);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [
    getSelectedSignals,
    isAllFilteredSelected,
    isNetworkTarget,
    isOutOfTestingScope,
    selectedCount,
    selectedSignalIds,
    signalById,
  ]);

  return count;
}

export function useSignalsBulkSend({
  selection,
  isNetworkTarget,
}: {
  selection: SignalsSelection;
  isNetworkTarget: boolean;
}) {
  const tSignals = useTranslations('signals');
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const { canStartSignalAttack, startBulkSignalAttack } = useSignalAttackRun();
  const { attackWithCopilot } = useSignalCopilotAttack();
  const { application, isLoadingTestingScope } =
    useApplicationTestingScopeSource();
  const [isRunning, setIsRunning] = useState(false);
  const { selectedCount, getSelectedSignals, clear } = selection;
  const isOutOfTestingScope = useCallback(
    (signal: GroupedSignal) =>
      !getSignalTestingScopeEligibility({
        signal,
        application,
        isLoadingTestingScope,
      }).isEligible,
    [application, isLoadingTestingScope],
  );
  const outOfTestingScopeCount = useOutOfTestingScopeSelectionCount({
    selection,
    isNetworkTarget,
    isOutOfTestingScope,
  });
  const takeMutableSelection = useCallback(async () => {
    const mutableSelection = takeMutableArchiveSelection(
      await getSelectedSignals(),
      isArchivedEntity,
    );

    if (mutableSelection.status === 'empty') {
      toasterApi.error({ title: tLifecycle('archivedSignalActionTooltip') });

      return null;
    }

    return mutableSelection.items;
  }, [getSelectedSignals, tLifecycle]);
  const sendToCopilot = useCallback(async () => {
    if (selectedCount === 0 || selectedCount > SEND_TO_COPILOT_SIGNAL_LIMIT) {
      return;
    }

    setIsRunning(true);

    try {
      const signals = await takeMutableSelection();

      if (!signals) return;

      const addedSignalCount = attackWithCopilot(signals);

      toasterApi.success({
        title: tSignals('bulkActions.copilotSuccessTitle'),
        description: tSignals('bulkActions.copilotSuccessDescription', {
          count: addedSignalCount,
        }),
      });
      clear();
    } catch (error) {
      console.error('Failed to send signals to Co-pilot:', error);
      toasterApi.error({
        title: tSignals('bulkActions.copilotErrorTitle'),
        description: tSignals('bulkActions.copilotErrorDescription'),
      });
    } finally {
      setIsRunning(false);
    }
  }, [attackWithCopilot, clear, selectedCount, tSignals, takeMutableSelection]);
  const sendToNeo = useCallback(async () => {
    if (
      !canStartSignalAttack ||
      selectedCount === 0 ||
      selectedCount > SEND_TO_NEO_SIGNAL_LIMIT
    ) {
      return;
    }

    setIsRunning(true);

    try {
      const signals = await takeMutableSelection();

      if (!signals) return;

      const eligibleSignals = isNetworkTarget
        ? signals
        : signals.filter((signal) => !isOutOfTestingScope(signal));

      if (eligibleSignals.length === 0) {
        toasterApi.error({
          title: tSignals('bulkActions.neoOutOfTestingScopeTitle'),
          description: tSignals('bulkActions.neoOutOfTestingScopeDescription'),
        });

        return;
      }

      await startBulkSignalAttack({
        signalIds: eligibleSignals.map((signal) => signal.id),
      });
      clear();
    } finally {
      setIsRunning(false);
    }
  }, [
    canStartSignalAttack,
    clear,
    isNetworkTarget,
    isOutOfTestingScope,
    selectedCount,
    startBulkSignalAttack,
    tSignals,
    takeMutableSelection,
  ]);

  return {
    isRunning,
    outOfTestingScopeCount,
    sendToCopilot,
    sendToNeo,
  };
}
